package com.shadowings.gero.data.lessons.chapter16

import com.shadowings.gero.data.lessons.DistractorPool
import com.shadowings.gero.data.lessons.NounAdjectivePhrase
import com.shadowings.gero.data.lessons.calculateNounAdjectivePhrases
import com.shadowings.gero.data.lessons.inflectAdjectiveGardiner
import com.shadowings.gero.data.lessons.inflectAdjectiveReading
import com.shadowings.gero.data.lessons.substantivizedAdjectiveLabel
import com.shadowings.gero.data.repository.AdjectivePairs
import com.shadowings.gero.data.repository.DatabaseRepositoryImpl
import com.shadowings.gero.domain.model.Characters
import com.shadowings.gero.domain.model.ExplanationContent
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.IExplanation
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.Option
import com.shadowings.gero.domain.model.QuizText
import com.shadowings.gero.domain.model.SingleSelectionContent
import com.shadowings.gero.domain.model.TrueFalseContent
import com.shadowings.gero.presentation.model.Word
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.exp_content_aggettivo_sostantivato_1
import gero.composeapp.generated.resources.exp_content_aggettivo_sostantivato_2
import gero.composeapp.generated.resources.exp_content_aggettivo_sostantivato_3
import gero.composeapp.generated.resources.exp_content_aggettivo_sostantivato_4
import gero.composeapp.generated.resources.exp_content_aggettivo_sostantivato_5
import gero.composeapp.generated.resources.exp_content_aggettivo_sostantivato_6
import gero.composeapp.generated.resources.exp_content_aggettivo_sostantivato_7
import gero.composeapp.generated.resources.exp_content_aggettivo_sostantivato_8
import gero.composeapp.generated.resources.exp_content_aggettivo_sostantivato_9
import gero.composeapp.generated.resources.exp_title_aggettivo_sostantivato
import gero.composeapp.generated.resources.quiz_aggettivo_sostantivato_da_solo
import gero.composeapp.generated.resources.quiz_aggettivo_sostantivato_tf_1
import gero.composeapp.generated.resources.quiz_aggettivo_sostantivato_tf_2
import gero.composeapp.generated.resources.quiz_aggettivo_sostantivato_tf_3
import gero.composeapp.generated.resources.quiz_aggettivo_sostantivato_tf_4
import gero.composeapp.generated.resources.quiz_ripasso_cosa_significa_gero
import gero.composeapp.generated.resources.quiz_ripasso_quale_gero_significa
import gero.composeapp.generated.resources.quiz_ripasso_traslitterazione
import kotlin.random.Random

fun getAggettivoSostantivatoContent(): List<IExplanation> {
    return listOf(
        ExplanationContent(
            title = Res.string.exp_title_aggettivo_sostantivato,
            content = Res.string.exp_content_aggettivo_sostantivato_1,
            face = Faces.Serious,
            character = Characters.Zeta,
        ),
        ExplanationContent(
            title = Res.string.exp_title_aggettivo_sostantivato,
            content = Res.string.exp_content_aggettivo_sostantivato_2,
            face = Faces.Doubt,
            character = Characters.Zeta,
            composable = mdcSlide("F35"),
        ),
        ExplanationContent(
            title = Res.string.exp_title_aggettivo_sostantivato,
            content = Res.string.exp_content_aggettivo_sostantivato_3,
            face = Faces.Happy,
            character = Characters.Alyn,
        ),
        ExplanationContent(
            title = Res.string.exp_title_aggettivo_sostantivato,
            content = Res.string.exp_content_aggettivo_sostantivato_4,
            face = Faces.Surprised,
            character = Characters.Zeta,
        ),
        ExplanationContent(
            title = Res.string.exp_title_aggettivo_sostantivato,
            content = Res.string.exp_content_aggettivo_sostantivato_5,
            face = Faces.Idle,
            character = Characters.Alyn,
        ),
        ExplanationContent(
            title = Res.string.exp_title_aggettivo_sostantivato,
            content = Res.string.exp_content_aggettivo_sostantivato_6,
            face = Faces.Idle,
            character = Characters.Alyn,
            composable = mdcSlide("F35"),
        ),
        ExplanationContent(
            title = Res.string.exp_title_aggettivo_sostantivato,
            content = Res.string.exp_content_aggettivo_sostantivato_7,
            face = Faces.Idle,
            character = Characters.Alyn,
            composable = mdcSlide("F35h2:X1"),
        ),
        ExplanationContent(
            title = Res.string.exp_title_aggettivo_sostantivato,
            content = Res.string.exp_content_aggettivo_sostantivato_8,
            face = Faces.Idle,
            character = Characters.Zeta,
        ),
        ExplanationContent(
            title = Res.string.exp_title_aggettivo_sostantivato,
            content = Res.string.exp_content_aggettivo_sostantivato_9,
            face = Faces.Happy,
            character = Characters.Alyn,
        ),
    )
}

// The adjectives met so far, except tutto (nb), which doesn't stand alone as a noun.
private val substantivizedAdjectiveIds = listOf(
    "BUONO", "GRANDE", "PICCOLO", "ECCELLENTE", "CATTIVO", "FORTE", "PURO", "VERO"
)

// The adjective alone, masculine ("the good one") or feminine ("the good thing").
private data class SubstantivizedAdjective(
    val adjective: Word,
    val feminine: Boolean,
    val hieroglyphs: String,
    val reading: String,
    val label: String
)

private fun substantivize(adjective: Word, feminine: Boolean, languageCode: String) = SubstantivizedAdjective(
    adjective = adjective,
    feminine = feminine,
    hieroglyphs = inflectAdjectiveGardiner(adjective, feminine, plural = false),
    reading = inflectAdjectiveReading(adjective, feminine, plural = false),
    label = substantivizedAdjectiveLabel(adjective, languageCode, feminine)
)

// 3 "what does it mean" + 3 "pick the hieroglyphs" on the adjective alone, both genders in each,
// + 2 "which one is an adjective used as a noun" against noun + adjective phrases
// + 2 true/false on the rule. The wrong options are the other gender, the same adjective with a
// noun (the G1 phrase) and another adjective in the same gender.
fun getAggettivoSostantivatoQuizContent(
    words: List<Word>,
    languageCode: String,
    random: Random = Random,
    adjectivePairs: List<AdjectivePairs> = DatabaseRepositoryImpl.adjectivePairs
): List<IQuizContent> {
    val known = DistractorPool.words(words).associateBy { it.id }
    val forms = substantivizedAdjectiveIds
        .mapNotNull { known[it] }
        .flatMap { adjective -> listOf(false, true).map { substantivize(adjective, it, languageCode) } }
    val phrases = calculateNounAdjectivePhrases(words, adjectivePairs, languageCode, plural = false)

    val (feminine, masculine) = forms.shuffled(random).partition { it.feminine }
    val (moreMasculine, moreFeminine) = if (random.nextBoolean()) 2 to 1 else 1 to 2
    val read = masculine.take(moreMasculine) + feminine.take(moreFeminine)
    val write = masculine.drop(moreMasculine).take(3 - moreMasculine) +
        feminine.drop(moreFeminine).take(3 - moreFeminine)

    val theory = listOf(
        TrueFalseContent(Res.string.quiz_aggettivo_sostantivato_tf_1, true),
        TrueFalseContent(Res.string.quiz_aggettivo_sostantivato_tf_2, false),
        TrueFalseContent(Res.string.quiz_aggettivo_sostantivato_tf_3, true),
        TrueFalseContent(Res.string.quiz_aggettivo_sostantivato_tf_4, false),
    ).shuffled(random).take(2)

    return (
        read.map { buildMeaningChoice(it, forms, phrases, random) } +
            write.map { buildHieroglyphChoice(it, forms, phrases, random) } +
            List(2) { buildAloneChoice(forms, phrases, random) }.filterNotNull() +
            theory
        ).shuffled(random)
}

// The same adjective next to a noun of the same Egyptian gender, or any such phrase if the
// adjective has none in adjectives.tsv.
private fun withNoun(
    form: SubstantivizedAdjective,
    phrases: List<NounAdjectivePhrase>,
    random: Random
): NounAdjectivePhrase? {
    val sameGender = phrases.filter { it.noun.isFeminine == form.feminine }
    return sameGender.filter { it.adjective.id == form.adjective.id }.randomOrNull(random)
        ?: sameGender.randomOrNull(random)
}

private fun otherForms(form: SubstantivizedAdjective, forms: List<SubstantivizedAdjective>, random: Random) =
    listOfNotNull(forms.firstOrNull { it.adjective.id == form.adjective.id && it.feminine != form.feminine }) +
        forms.filter { it.adjective.id != form.adjective.id && it.feminine == form.feminine }.shuffled(random) +
        forms.filter { it.adjective.id != form.adjective.id && it.feminine != form.feminine }.shuffled(random)

private fun options(correct: String, wrong: List<String>, random: Random): List<Option> =
    (listOf(Option(correct, correct = true)) +
        wrong.filter { it != correct }.distinct().take(3).map { Option(it, correct = false) })
        .shuffled(random)

private fun buildMeaningChoice(
    form: SubstantivizedAdjective,
    forms: List<SubstantivizedAdjective>,
    phrases: List<NounAdjectivePhrase>,
    random: Random
): SingleSelectionContent {
    val others = otherForms(form, forms, random).map { it.label }
    val wrong = listOfNotNull(others.firstOrNull(), withNoun(form, phrases, random)?.correctPhrase) + others.drop(1)
    return SingleSelectionContent(
        text = Res.string.quiz_ripasso_cosa_significa_gero,
        detail = form.hieroglyphs,
        options = options(form.label, wrong, random),
        info = QuizText.Resource(Res.string.quiz_ripasso_traslitterazione, listOf(form.reading))
    )
}

private fun buildHieroglyphChoice(
    form: SubstantivizedAdjective,
    forms: List<SubstantivizedAdjective>,
    phrases: List<NounAdjectivePhrase>,
    random: Random
): SingleSelectionContent {
    val others = otherForms(form, forms, random).map { it.hieroglyphs }
    val wrong = listOfNotNull(others.firstOrNull(), withNoun(form, phrases, random)?.hieroglyphs) + others.drop(1)
    return SingleSelectionContent(
        text = Res.string.quiz_ripasso_quale_gero_significa,
        textArgs = listOf(form.label),
        detail = "",
        options = options(form.hieroglyphs, wrong, random)
    )
}

// One adjective alone among three noun + adjective phrases, each on a different noun.
private fun buildAloneChoice(
    forms: List<SubstantivizedAdjective>,
    phrases: List<NounAdjectivePhrase>,
    random: Random
): SingleSelectionContent? {
    val alone = forms.randomOrNull(random) ?: return null
    val withNouns = phrases.groupBy { it.noun.id }.values.shuffled(random).take(3).map { it.random(random).hieroglyphs }
    if (withNouns.size < 3) return null
    return SingleSelectionContent(
        text = Res.string.quiz_aggettivo_sostantivato_da_solo,
        detail = "",
        options = options(alone.hieroglyphs, withNouns, random)
    )
}
